import cloneDeep from 'lodash/cloneDeep';
import get from 'lodash/get';
import set from 'lodash/set';
import isEmpty from 'lodash/isEmpty';
// modules
import { LStorage } from 'modules/Storage';
import Settings from 'modules/Settings/Settings';
import { SettingsType } from 'modules/Settings/typings';
import LoggerService from 'modules/Logger';
import TradingChartService from 'modules/TradingChart/service';

class RecentLayoutService {
  recentLayoutId = '';

  getRecentLayout = () => {
    if (Settings.getIsChartSettingsSaveEnabled()) {
      return this.getRecentLayoutFromServer();
    }

    return this.chartsState;
  }

  setRecentLayoutId = (id: string) => {
    this.recentLayoutId = id;
  }

  getRecentLayoutFromServer = async () => {
    try {
      const data =
        await Settings.getChartSettingsContent({
          settingsType: SettingsType.SAVING_RECENT_LAYOUT,
        });

      if (isEmpty(data) || !data?.content) {
        return null;
      }
      this.setRecentLayoutId(data?.id || '');
      const recentChartLayoutContent = data?.content || '{}';
      const recentChartLayout = JSON.parse(recentChartLayoutContent);
      const clonedChartLayout = cloneDeep(recentChartLayout);
      const chartsData = get(clonedChartLayout, 'charts', []);
      const clonedChartData: ContentChartsType = cloneDeep(chartsData);
      let updatedChartsData: ContentChartsType = [];

      if (clonedChartLayout && clonedChartData.length > 0) {
        // sources present in the layout are in instrumentKey
        // so update the sources with TV token
        updatedChartsData = await TradingChartService
          .updateChartDataWithTVToken(clonedChartData);

        // For existing recent layout, drawings sources are available in first layout
        // If present, add the same drawings to other layouts
        const drawingsSources = this.getDrawingsSourcesFromRecentLayout(
          updatedChartsData);

        if (drawingsSources?.length > 0) {
          // If drawings sources are available in first layout, add the same to other layouts
          this.addDrawingSourcesInRecentLayout(
            updatedChartsData, drawingsSources,
          );
        } else {
          // If drawings sources are not available in first layout, fetch them for each layout
          updatedChartsData = await this.fetchAndAddDrawingsInRecentLayout(
            updatedChartsData,
          );
        }
        set(clonedChartLayout, 'charts', updatedChartsData);
      }
      // eslint-disable-next-line @typescript-eslint/no-unsafe-return
      return clonedChartLayout || null;
    } catch (err) {
      LoggerService.error(err);
    }
  };

  fetchAndAddDrawingsInRecentLayout = async (
    chartsData: ContentChartsType,
  ) => {
    const charts: ContentChartsType = cloneDeep(chartsData);

    const processedCharts: (ContentChartType | null)[] = await Promise.all(
      charts.map(async (chart) => {
        const panes: ContentPanes = get(chart, 'panes', []);
        if (panes?.length > 0) {
          let isMainSeriesPresent = false;
          const drawingSources = await this
            .getRecentLayoutDrawingSources(chart.chartId);
          // Check if main series source is present in any of the panes
          const filteredPanes = panes.filter((pane) => {
            const sources = get(pane, 'sources', []);
            // Check if main series source is present in the pane
            const isMainSeriesSource = sources
              .some((source) => get(source, 'type') === 'MainSeries');
            if (isMainSeriesSource) {
              set(pane, 'sources', [...sources, ...drawingSources]);
              isMainSeriesPresent = true;
            }
            return sources.length > 0;
          });
          set(chart, 'panes', filteredPanes);
          // If main series source is present in any of the panes, return the chart
          if (isMainSeriesPresent) {
            return chart;
          }
        }
        // Return null for charts that doesn't have main series source
        return null;
      }),
    );

    // Filter out null charts that don't have main series source
    const updatedCharts: ContentChartsType = processedCharts
      .filter((chart): chart is ContentChartType => chart !== null);
    return updatedCharts;
  }

  getDrawingsSourcesFromRecentLayout = (chartData: ContentChartsType) => {
    // In the layouts saved, only first layout will have drawing sources
    const firstChartLayout = chartData?.[0];
    const firstChartLayoutPanes = get(firstChartLayout, 'panes', []) || [];
    let drawingSources: ContentSources = [];

    // Extracting drawing sources from first chart layout sources
    if (firstChartLayoutPanes?.length > 0) {
      const mainSeriesPane = TradingChartService.getMainSeriesPane(
        firstChartLayoutPanes,
      );
      const mainSeriesSources = get(mainSeriesPane, 'sources', []);
      if (mainSeriesSources?.length > 0) {
        drawingSources = mainSeriesSources.filter((source) => {
          const sourceState = get(source, 'state') || {};
          const symbol = get(sourceState, 'symbol') ?? '';
          const type = get(source, 'type');
          return type !== 'MainSeries' && !!symbol;
        });
      }
    }

    return drawingSources;
  }

  addDrawingSourcesInRecentLayout = (
    chartData: ContentChartsType,
    drawingSources: ContentSources,
  ) => {
    const chartLayouts = chartData?.slice(1) || [];

    if (chartLayouts.length > 0) {
      chartLayouts.forEach((chart) => {
        const panes = get(chart, 'panes', []);
        if (panes?.length > 0) {
          const mainSeriesPane = TradingChartService.getMainSeriesPane(panes);
          if (mainSeriesPane) {
            const mainSeriesSources = get(mainSeriesPane, 'sources', []);
            set(mainSeriesPane, 'sources', [...mainSeriesSources, ...drawingSources]);
          }
        }
      });
    }
  }

  async getRecentLayoutDrawingSources(chartId: string) {
    const drawingKey = TradingChartService.getDrawingKey(
      this.recentLayoutId, chartId);

    const data = await Settings.getChartSettingsContent({
      id: `${this.recentLayoutId}/${chartId}`,
      settingsType: SettingsType.SAVING_DRAWINGS,
      loadingKey: `CHART_SETTING_DATA_LOADING_${drawingKey}` as LoadingKey,
    });

    const rawSources = TradingChartService.getLineToolsAndGroupsSources(
      data?.content || '',
    );

    if (isEmpty(rawSources)) {
      return [];
    }

    // Update drawings sources with TV token
    const updatedSources = await TradingChartService
      .updateDrawingsSourcesWithTVToken(rawSources);

    // convert map to array
    const drawingsSources = Object.values(
      Object.fromEntries(updatedSources),
    ).map((source) => source?.state);

    return drawingsSources;
  }

  get chartsState() {
    return LStorage.getItem<BaseObject>('TV_CHART_STATE');
  }
}

export default new RecentLayoutService();
